import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { SLAPolicy } from "../types";
import SLAPolicyEditor from "../components/SLAPolicyEditor";
import ReplyTemplateManager from "../components/ReplyTemplateManager";

/**
 * HelpDesk → Settings.
 *
 * Helpdesk-wide policy that is not part of the mail wizard. The global SLA
 * policy used to sit on Admin → Settings, a page that had accumulated logo
 * uploads, installers, auth policy and timezone alongside it - so the one
 * setting a helpdesk manager actually tunes was buried among appliance admin.
 * Same move that already took alert thresholds to Monitoring & Alerts →
 * Settings and patch approval to Devices → Patch Compliance.
 *
 * Mail identity, DNS and the notification recipient stay on HelpDesk → Mail setup:
 * that is a guided wizard with an order to it, and dropping SLA targets into
 * the middle of it would break the sequence.
 */
export default function HelpDeskSettings() {
  const [sla, setSla] = useState<SLAPolicy | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/api/v1/sla-policies/global");
      setSla(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Could not load the SLA policy");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!sla) return;
    setBusy(true);
    try {
      // The editor round-trips the whole row; the API takes only the minute
      // fields, so strip the identity/audit columns before PUTting.
      const { id: _id, customer_id: _cid, updated_at: _ua, ...minutes } = sla;
      void _id;
      void _cid;
      void _ua;
      await api.put("/api/v1/sla-policies/global", minutes);
      setSaved(true);
      setError(null);
      await load();
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold dark:text-gray-100">HelpDesk settings</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Service targets for the ticket queue. Mail addresses, DNS and who gets notified live
          on{" "}
          <Link to="/helpdesk/setup" className="text-blue-600 dark:text-blue-400 hover:underline">
            HelpDesk → Mail setup
          </Link>
          .
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          SLA policy (global)
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Targets for first response (a tech adds a comment) and resolution (ticket moves to
          resolved/closed). Measured in <strong>active time</strong> — on-hold periods pause the
          clock. Per-customer overrides live on each customer's page.
        </p>
        {sla ? (
          <>
            <SLAPolicyEditor value={sla} onChange={setSla} />
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={save}
                disabled={busy}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded-lg"
              >
                {busy ? "Saving…" : "Save SLA policy"}
              </button>
              {saved && (
                <span className="text-xs text-green-600 dark:text-green-400">Saved</span>
              )}
            </div>
          </>
        ) : (
          !error && <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        )}
      </div>

      <ReplyTemplateManager />
    </div>
  );
}
